import axios from "axios";
import store from "../../store/index.js";
import { StorageService } from "./StorageService.js";

const DEFAULT_HEADERS = { "Content-Type": "application/json" };

/**
 * Get JWT token from localStorage
 */
function getAuthToken() {
  return StorageService.getAuthToken();
}

/**
 * Setup request interceptor with JWT token and headers
 */
function setupAuthInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    // Don't set Content-Type for FormData (file uploads)
    // Let axios handle it with the proper multipart boundary
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = DEFAULT_HEADERS["Content-Type"];
    }
    
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  });
  return instance;
}

/**
 * Setup response interceptor to handle 401 errors
 */
function setupErrorInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        StorageService.clearAuthToken();
      }
      return Promise.reject(error);
    }
  );
  return instance;
}

// Authenticated axios instance with JWT and error handling
const authenticatedAxiosInstance = setupErrorInterceptor(
  setupAuthInterceptor(axios.create({}))
);

/**
 * Create an authenticated axios instance with JWT token in Authorization header
 * Used for making authenticated requests from components
 */
export function createAuthenticatedAxiosInstance() {
  const instance = axios.create({});
  setupAuthInterceptor(instance);
  setupErrorInterceptor(instance);
  return instance;
}

/**
 * Generic request method to reduce code duplication
 * For GET/DELETE: returns full response object
 * For POST/PUT/PATCH: returns response.data
 */
async function request(instance, method, url, data = null, config = {}) {
  let response;
  
  if (method === 'get') {
    // GET: second param is config, not data
    response = await instance.get(url, { 
      headers: DEFAULT_HEADERS,
      ...config 
    });
  } else if (method === 'delete') {
    // DELETE: can have data payload
    const deleteConfig = { 
      headers: DEFAULT_HEADERS,
      ...config 
    };
    if (data) {
      deleteConfig.data = data;
    }
    response = await instance.delete(url, deleteConfig);
  } else {
    // POST/PUT/PATCH: second param is data, third is config
    response = await instance[method](url, data, { 
      headers: DEFAULT_HEADERS,
      ...config 
    });
  }
  
  // Return full response for GET/DELETE, just data for POST/PUT/PATCH
  return ['get', 'delete'].includes(method) ? response : response.data;
}

/**
 * Generic request method for authenticated requests
 */
async function authenticatedRequest(method, base_url, endpoint, data = null) {
  return request(authenticatedAxiosInstance, method, base_url + endpoint, data);
}

/**
 * Generic request method for unauthenticated requests
 */
async function publicRequest(method, base_url, endpoint, data = null, config = {}) {
  return request(axios, method, base_url + endpoint, data, config);
}

// ============ Authenticated Requests ============

export async function postWithAuthentication(base_url, url, params = {}) {
  return authenticatedRequest('post', base_url, url, params);
}

export async function getWithAuthentication(base_url, url, params = {}) {
  return authenticatedRequest('get', base_url, url, params);
}

export async function patchWithAuthentication(base_url, url, params = {}) {
  return authenticatedRequest('patch', base_url, url, params);
}

export async function deleteWithAuthentication(base_url, url, params = null) {
  return authenticatedRequest('delete', base_url, url, params);
}

// ============ Unauthenticated Requests ============

export async function get(base_url, url, data = {}) {
  return publicRequest('get', base_url, url, null, { params: data });
}

export async function post(base_url, url, params = {}) {
  return publicRequest('post', base_url, url, params);
}

export async function put(base_url, url, params = {}) {
  return publicRequest('put', base_url, url, params);
}
